import logger from '../../../../utils/logger';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStripe } from '@stripe/react-stripe-js';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  Landmark,
  Loader2,
} from 'lucide-react';
import addonPurchaseService from '../../../../services/addonPurchaseService';
import { COMPANIES_QUERY_KEY } from '../../../../hooks/useCompanies';

// Custom USDC icon component
const UsdcIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 32 32" fill="currentColor">
    <path d="M16 32C7.163 32 0 24.837 0 16S7.163 0 16 0s16 7.163 16 16-7.163 16-16 16zm-1.5-17.5v-3h3v3h1.5v-3h1.5v3.17c1.726.445 3 2.01 3 3.83 0 1.82-1.274 3.385-3 3.83V25.5h-1.5v-3h-1.5v3h-3v-3h-1.5v-3h1.5v-5h-1.5v-3h1.5zm3 5v2c.828 0 1.5-.672 1.5-1.5s-.672-1.5-1.5-1.5v1zm0-5v2c-.828 0-1.5.672-1.5 1.5s.672 1.5 1.5 1.5v-2h-1.5v-3h1.5z" />
  </svg>
);

/**
 * AddonPaymentComplete - Handles redirect return from Stripe's crypto checkout for addon purchases.
 *
 * Flow:
 * 1. User completes crypto payment on Stripe's hosted page
 * 2. Stripe redirects to /addon-payment-complete?payment_intent=pi_xxx&payment_intent_client_secret=xxx&registration=xxx
 * 3. This component verifies the payment status
 * 4. Confirms payment with backend via addonPurchaseService
 * 5. Redirects user to company banking page
 */
const AddonPaymentComplete = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stripe = useStripe();
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth0();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error' | 'processing'
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const hasVerified = useRef(false);

  // Get URL parameters
  const paymentIntentClientSecret = searchParams.get(
    'payment_intent_client_secret'
  );
  const registrationId = searchParams.get('registration');
  const redirectStatus = searchParams.get('redirect_status');

  useEffect(() => {
    // Wait for auth to load
    if (authLoading) return;

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/auth/sign-in/default', { replace: true });
      return;
    }

    // Prevent duplicate verification
    if (hasVerified.current) return;

    const verifyPayment = async () => {
      // Check for immediate redirect status from Stripe
      if (redirectStatus === 'failed') {
        setStatus('error');
        setErrorMessage(
          'El pago no fue completado. Por favor intenta de nuevo.'
        );
        return;
      }

      if (!stripe || !paymentIntentClientSecret) {
        // If no client secret, maybe the user navigated here directly
        if (!paymentIntentClientSecret) {
          setStatus('error');
          setErrorMessage(
            'No se encontró información del pago. Por favor vuelve a intentar el proceso de pago.'
          );
          return;
        }
        // Stripe not loaded yet, wait
        return;
      }

      hasVerified.current = true;
      setStatus('verifying');

      try {
        // Retrieve the PaymentIntent from Stripe
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(
          paymentIntentClientSecret
        );

        if (error) {
          logger.error('Error retrieving PaymentIntent:', error);
          setStatus('error');
          setErrorMessage(error.message || 'Error al verificar el pago');
          return;
        }

        setPaymentDetails({
          id: paymentIntent.id,
          amount: paymentIntent.amount / 100, // Convert from cents
          currency: paymentIntent.currency.toUpperCase(),
          status: paymentIntent.status,
        });

        // Check payment status
        switch (paymentIntent.status) {
          case 'succeeded':
            // Payment successful - confirm with backend
            setStatus('success');
            await confirmPaymentWithBackend(paymentIntent.id);

            // Invalidate the companies cache so the company list updates without a manual refresh
            queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
            break;

          case 'processing':
            // Payment still processing (common for crypto)
            setStatus('processing');
            break;

          case 'requires_payment_method':
            setStatus('error');
            setErrorMessage('El pago requiere un método de pago válido.');
            break;

          case 'requires_action':
            setStatus('error');
            setErrorMessage(
              'Se requiere acción adicional para completar el pago.'
            );
            break;

          default:
            setStatus('error');
            setErrorMessage(
              `Estado de pago inesperado: ${paymentIntent.status}`
            );
        }
      } catch (err) {
        logger.error('Payment verification error:', err);
        setStatus('error');
        setErrorMessage(err.message || 'Error al verificar el estado del pago');
      }
    };

    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stripe,
    paymentIntentClientSecret,
    registrationId,
    redirectStatus,
    isAuthenticated,
    authLoading,
    navigate,
  ]);

  // Auto-redirect after successful payment
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }

    // When countdown reaches 0, redirect to company banking page
    if (status === 'success' && countdown === 0) {
      if (registrationId) {
        navigate(`/empresa/${registrationId}/banking`, { replace: true });
      } else {
        navigate('/empresa/home', { replace: true });
      }
    }
  }, [status, countdown, registrationId, navigate]);

  /**
   * Confirms payment with the backend using addonPurchaseService.
   */
  const confirmPaymentWithBackend = async (intentId) => {
    try {
      const token = await getAccessTokenSilently();
      addonPurchaseService.setAccessToken(token);

      const response = await addonPurchaseService.confirmAddonPayment(intentId);
      logger.debug('[AddonPaymentComplete] Backend confirmation:', response);

      if (!response.success) {
        logger.warn(
          '[AddonPaymentComplete] Backend confirmation warning:',
          response.error
        );
        // Don't fail the flow - webhook will handle it
      }
    } catch (err) {
      logger.warn('[AddonPaymentComplete] Backend confirmation error:', err);
      // Don't fail the flow - webhook will handle it
    }
  };

  /**
   * Handles the continue button click.
   */
  const handleContinue = () => {
    if (registrationId) {
      navigate(`/empresa/${registrationId}/banking`, { replace: true });
    } else {
      navigate('/empresa/home', { replace: true });
    }
  };

  /**
   * Handles retry for failed payments.
   */
  const handleRetry = () => {
    if (registrationId) {
      navigate(`/empresa/${registrationId}/banking`, { replace: true });
    } else {
      navigate('/empresa/home', { replace: true });
    }
  };

  // Loading state
  if (authLoading || (status === 'verifying' && !stripe)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--rely-surface-page)] p-4 text-[var(--rely-text-strong)]">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-[var(--rely-color-brand-green)]" />
          <p className="text-lg text-[var(--rely-text-muted)]">
            Cargando...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="addon-payment-complete-shell"
      className="flex min-h-screen items-center justify-center bg-[var(--rely-surface-page)] p-4 text-[var(--rely-text-strong)]"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="addon-payment-complete-panel"
        className="relative w-full max-w-md rounded-[var(--rely-radius-panel)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] p-6 shadow-[var(--rely-shadow-panel)] sm:p-8"
      >
        {/* Verifying State */}
        {status === 'verifying' && (
          <div className="relative text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-[var(--rely-radius-surface)] bg-[var(--rely-surface-elevated)] text-[var(--rely-color-brand-green)] ring-1 ring-[var(--rely-border-subtle)]">
              <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
            </div>
            <h2 className="mb-2 text-2xl font-semibold text-[var(--rely-text-strong)]">
              Verificando pago
            </h2>
            <p className="text-[var(--rely-text-muted)]">
              Confirmando tu transacción en la blockchain...
            </p>
          </div>
        )}

        {/* Processing State */}
        {status === 'processing' && (
          <div className="relative text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-[var(--rely-radius-surface)] bg-[var(--rely-surface-elevated)] text-[var(--rely-text-muted)] ring-1 ring-[var(--rely-border-subtle)]"
            >
              <Loader2 className="h-8 w-8" aria-hidden="true" />
            </motion.div>
            <h2 className="mb-2 text-2xl font-semibold text-[var(--rely-text-strong)]">
              Procesando en blockchain
            </h2>
            <p className="mb-6 text-[var(--rely-text-muted)]">
              Tu pago con USDC está siendo confirmado. Esto puede tomar unos
              minutos.
            </p>
            {paymentDetails && (
              <div
                data-testid="addon-payment-complete-amount"
                className="mb-6 rounded-[var(--rely-radius-surface)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] p-5"
              >
                <p className="text-sm text-[var(--rely-text-muted)]">Monto</p>
                <p className="text-3xl font-semibold text-[var(--rely-text-strong)]">
                  ${paymentDetails.amount}{' '}
                  <span className="text-lg text-[var(--rely-text-muted)]">USDC</span>
                </p>
              </div>
            )}
            <button
              onClick={handleContinue}
              className="group w-full rounded-[var(--rely-radius-control)] bg-[var(--rely-color-brand-green)] px-6 py-4 font-semibold text-[var(--rely-color-brand-black)] transition-[box-shadow,transform] duration-200 hover:shadow-[0_14px_30px_rgba(113,222,128,0.24)] active:translate-y-px"
            >
              <span className="flex items-center justify-center gap-2">
                Continuar
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </button>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="relative text-center">
            {/* Animated check */}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-[var(--rely-radius-surface)] bg-[var(--rely-surface-selected)] text-[var(--rely-color-brand-green)] ring-1 ring-[var(--rely-color-brand-green)]/35"
            >
              <svg className="h-12 w-12" viewBox="0 0 52 52" aria-hidden="true">
                <motion.circle
                  cx="26"
                  cy="26"
                  r="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
                <motion.path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14 27l8 8 16-16"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.4, ease: 'easeOut' }}
                />
              </svg>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="mb-2 text-3xl font-semibold text-[var(--rely-text-strong)]">
                ¡Pago exitoso!
              </h2>
              <p className="mb-6 text-[var(--rely-text-muted)]">
                Tu servicio adicional ha sido agregado correctamente
              </p>
            </motion.div>

            {paymentDetails && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                data-testid="addon-payment-complete-amount"
                className="mb-6 rounded-[var(--rely-radius-surface)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] p-5"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <p className="text-sm text-[var(--rely-text-muted)]">Monto pagado</p>
                    <p className="text-3xl font-semibold text-[var(--rely-text-strong)]">
                      ${paymentDetails.amount}{' '}
                      <span className="text-lg text-[var(--rely-text-muted)]">USDC</span>
                    </p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-[var(--rely-radius-control)] bg-[var(--rely-surface-panel)] text-[var(--rely-text-muted)]">
                    <UsdcIcon className="h-6 w-6" />
                  </div>
                </div>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <button
                onClick={handleContinue}
                className="group w-full rounded-[var(--rely-radius-control)] bg-[var(--rely-color-brand-green)] px-6 py-4 font-semibold text-[var(--rely-color-brand-black)] transition-[box-shadow,transform] duration-200 hover:shadow-[0_14px_30px_rgba(113,222,128,0.24)] active:translate-y-px"
              >
                <span className="flex items-center justify-center gap-2">
                  <Landmark className="h-5 w-5" aria-hidden="true" />
                  Volver a Servicios Financieros
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-5 text-center text-sm text-[var(--rely-text-muted)]"
            >
              <div className="flex items-center justify-center gap-2">
                <div className="h-2 w-2 animate-pulse rounded-full bg-[var(--rely-color-brand-green)]" />
                Redirigiendo en {countdown}...
              </div>
            </motion.div>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="relative text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-[var(--rely-radius-surface)] bg-red-500/10 text-red-500 ring-1 ring-red-500/30 dark:text-red-300"
            >
              <AlertCircle className="h-8 w-8" aria-hidden="true" />
            </motion.div>
            <h2 className="mb-2 text-2xl font-semibold text-[var(--rely-text-strong)]">
              Error en el pago
            </h2>
            <p className="mb-6 text-[var(--rely-text-muted)]">{errorMessage}</p>
            <button
              onClick={handleRetry}
              className="group flex w-full items-center justify-center gap-2 rounded-[var(--rely-radius-control)] border border-red-500/30 bg-red-500/10 px-6 py-4 font-semibold text-red-600 transition-colors hover:bg-red-500/15 dark:text-red-300"
            >
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
              Intentar de nuevo
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AddonPaymentComplete;
